/**
 * Suggest invoice brand colours taken from the tenant's own logo.
 *
 * Everything here runs in the browser: the logo is drawn to an offscreen
 * canvas and sampled. Vercel Blob serves `access-control-allow-origin: *`, so
 * `crossOrigin="anonymous"` keeps the canvas untainted for stored logos as well
 * as for a locally-picked File.
 */

/** Sampling size. Small on purpose — we want dominant colours, not detail. */
const SAMPLE_EDGE = 64;
/** Quantisation: 5 levels per channel is coarse enough to merge gradients. */
const LEVELS = 5;
/** The renderer paints white text on this colour. */
const MIN_WHITE_CONTRAST = 4.5;
/** Below this the darkened result is effectively black and not worth offering. */
const NEAR_BLACK_MAX = 30;

function srgbToLinear(c: number): number {
  const v = c / 255;
  return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
}

function luminanceRgb(r: number, g: number, b: number): number {
  return 0.2126 * srgbToLinear(r) + 0.7152 * srgbToLinear(g) + 0.0722 * srgbToLinear(b);
}

/** Contrast of white text against this colour. */
function whiteContrastRgb(r: number, g: number, b: number): number {
  return 1.05 / (luminanceRgb(r, g, b) + 0.05);
}

const toHex = (r: number, g: number, b: number): string =>
  "#" + [r, g, b].map((v) => Math.round(v).toString(16).padStart(2, "0")).join("");

/**
 * Darken toward black until white text is legible on it, preserving hue.
 *
 * Suggesting a logo's raw dominant colour is usually wrong: brand colours are
 * often bright (an orange logo measured 2.6:1), which is exactly the state the
 * contrast warning exists to prevent. Scaling RGB uniformly keeps the hue and
 * saturation ratio while dropping lightness, so the suggestion still reads as
 * "your brand" but is actually usable on the invoice.
 *
 * Returns null when the colour cannot be made legible without collapsing to
 * near-black. Such a swatch is worth skipping rather than showing: it no longer
 * resembles the brand it came from, and several of them are indistinguishable
 * from each other, so they add noise instead of choice.
 */
function darkenUntilLegible(r: number, g: number, b: number): string | null {
  let scale = 1;
  for (let i = 0; i < 24; i++) {
    const rr = r * scale;
    const gg = g * scale;
    const bb = b * scale;
    if (whiteContrastRgb(rr, gg, bb) >= MIN_WHITE_CONTRAST) {
      // Collapsed to near-black — no brand identity left, so skip it.
      if (Math.max(rr, gg, bb) < NEAR_BLACK_MAX) return null;
      return toHex(rr, gg, bb);
    }
    scale *= 0.9;
  }
  return null;
}

/** Perceptual-ish distance, so suggestions are visibly different from each other. */
function tooSimilar(a: string, b: string): boolean {
  const pa = a.slice(1).match(/../g)!.map((h) => parseInt(h, 16));
  const pb = b.slice(1).match(/../g)!.map((h) => parseInt(h, 16));
  const d = Math.abs(pa[0] - pb[0]) + Math.abs(pa[1] - pb[1]) + Math.abs(pa[2] - pb[2]);
  return d < 60;
}

/**
 * Extract up to `max` legible brand-colour suggestions from a logo URL.
 *
 * Resolves to [] rather than throwing for every failure mode — a missing logo,
 * a CORS-tainted canvas, a monochrome mark. The caller then renders no
 * suggestion row at all and the user picks a colour by hand; suggestions are a
 * convenience and are never worth an error state.
 */
export function extractLogoColors(url: string, max = 4): Promise<string[]> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onerror = () => resolve([]);
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = SAMPLE_EDGE;
        canvas.height = SAMPLE_EDGE;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        if (!ctx) return resolve([]);
        ctx.drawImage(img, 0, 0, SAMPLE_EDGE, SAMPLE_EDGE);
        const { data } = ctx.getImageData(0, 0, SAMPLE_EDGE, SAMPLE_EDGE);

        const buckets = new Map<string, { n: number; r: number; g: number; b: number }>();
        for (let i = 0; i < data.length; i += 4) {
          const [r, g, b, a] = [data[i], data[i + 1], data[i + 2], data[i + 3]];
          // Skip transparent padding — otherwise PNG edges muddy every bucket.
          if (a < 128) continue;
          // Skip near-white and near-black: they carry no brand identity and
          // would dominate most logos (backgrounds, outlines, text).
          const max3 = Math.max(r, g, b);
          const min3 = Math.min(r, g, b);
          if (max3 > 244 && min3 > 244) continue;
          if (max3 < 26) continue;
          // Skip greys — a colourless bucket is not a brand colour.
          if (max3 - min3 < 18) continue;

          const q = (v: number): number => Math.round((v / 255) * (LEVELS - 1));
          const key = `${q(r)},${q(g)},${q(b)}`;
          const cur = buckets.get(key);
          if (cur) {
            cur.n++;
            cur.r += r;
            cur.g += g;
            cur.b += b;
          } else {
            buckets.set(key, { n: 1, r, g, b });
          }
        }

        const ranked = [...buckets.values()]
          .sort((a, b) => b.n - a.n)
          // Average within the bucket so the swatch is the real colour, not the
          // quantised centroid.
          .map((c) => ({ r: c.r / c.n, g: c.g / c.n, b: c.b / c.n }));

        const out: string[] = [];
        for (const c of ranked) {
          if (out.length >= max) break;
          const hex = darkenUntilLegible(c.r, c.g, c.b);
          if (!hex) continue;
          if (out.some((existing) => tooSimilar(existing, hex))) continue;
          out.push(hex);
        }
        resolve(out);
      } catch {
        // Tainted canvas or any decode failure — suggestions are optional.
        resolve([]);
      }
    };
    img.src = url;
  });
}
